import type {
  AgeLimitRestrictionT,
  CoverageMessageT,
  CoverageResourceT,
  CoverageRestrictionsT,
  FormularyResponseT,
  PayerAlternativeT,
  PayerCopayT,
  PayerInformationT,
  PrescriptionBenefitsResponseT,
  QuantityLimitRestrictionT,
  StepMedicationRestrictionT,
  TherapeuticAlternativesT,
} from '@lotushealth/lotushealth_js_sdk/schemas';
import {
  ArrowRight,
  CreditCard,
  DollarSign,
  Eye,
  ExternalLink,
  FileText,
  Info,
  ListTree,
  MessageSquare,
  Pill,
  ShieldCheck,
} from 'lucide-react';
import { useState } from 'react';

import type { RxMedication } from '@/entities/rx';
import { Button } from '@/shared/ui/shadcn/button';
import { cn } from '@/lib/utils';

import { FormularyDetailDialog } from './FormularyDetailDialog';

import {
  type FormularyDisplayState,
  bucketFormularyStatus,
  describeCopay,
  deriveFormularyDisplayState,
  formularyStatusLabel,
  pharmacyTypeLabel,
  pickAlternativeRetailCopay,
} from '../model/formularyDisplayPolicy';

export interface BenefitsCoordinationPanelProps {
  payerInformation: PayerInformationT | null | undefined;
  formulary: FormularyResponseT | null | undefined;
  benefits: PrescriptionBenefitsResponseT | null | undefined;
  /** The currently-selected medication, used for multi-ingredient + obsolete policy. */
  medication: RxMedication | null | undefined;
  /**
   * Optional callback invoked when the clinician picks a payer-preferred
   * or therapeutic alternative. The integer is a DoseSpot
   * ``DispensableDrugId`` ready to be passed to ``select_medication``.
   * When omitted, alternatives render as read-only.
   *
   * May return a promise — callers can await the underlying swap before
   * dismissing, e.g. the Details-step modal closes after the swap
   * completes. The return value is ignored either way.
   */
  onSelectAlternative?: (dispensableDrugId: number) => void | Promise<void>;
  /** Disable the alternative buttons, e.g. while a switch is in flight. */
  isSelectingAlternative?: boolean;
}

/**
 * Displays BenefitsCoordination data per ACR PA.109.1 and the F1–F7
 * cert matrix — payer coverage details, formulary status, drug-level
 * restrictions, copays, and alternatives.
 *
 * Sections (rendered top-to-bottom in order of decision relevance):
 *
 * 1. Payer (271 response): which plan / member id we will be billing.
 * 2. Formulary status + flags + multi-channel copays + restriction
 *    details (step meds, qty limits, age/gender, messages, links).
 * 3. Payer-preferred alternatives — sorted by preference level.
 * 4. Therapeutic alternatives — only multi-ingredient when target is.
 * 5. RTPB pricing: real-time benefits cost data when present.
 *
 * Display policy (Unknown/Obsolete suppression, payer-vs-therapeutic
 * mutex, multi-ingredient filter) is centralised in
 * :mod:`formularyDisplayPolicy`.
 */
export function BenefitsCoordinationPanel({
  payerInformation,
  formulary,
  benefits,
  medication,
  onSelectAlternative,
  isSelectingAlternative = false,
}: BenefitsCoordinationPanelProps) {
  const hasAnyData = payerInformation || formulary || benefits;
  if (!hasAnyData) return null;

  const display = deriveFormularyDisplayState({ formulary, medication });
  const pricing = extractPricing(benefits);
  const formularyStatus = formulary?.FormularyStatus ?? null;
  const restrictions = formulary?.Restrictions ?? null;
  const copays = formulary?.Copays ?? null;

  return (
    <div
      className={cn(
        'rounded-lg border border-border/60 bg-muted/30 p-4',
        'dark:border-border/40 dark:bg-muted/20'
      )}
    >
      <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        <CreditCard className="size-3.5" aria-hidden />
        Benefits Coordination
      </div>

      <div className="space-y-4">
        {payerInformation ? <PayerSection payer={payerInformation} /> : null}

        {formularyStatus || restrictions || (copays?.length ?? 0) > 0 ? (
          <FormularySection
            formularyStatus={formularyStatus}
            restrictions={restrictions}
            copays={copays}
            display={display}
            payerInformation={payerInformation}
          />
        ) : null}

        {display.policy.notice ? <NoticeRow text={display.policy.notice} /> : null}

        {display.payerAlternatives.length > 0 ? (
          <PayerAlternativesSection
            alternatives={display.payerAlternatives}
            onSelectAlternative={onSelectAlternative}
            isSelectingAlternative={isSelectingAlternative}
          />
        ) : null}

        {display.therapeuticAlternatives.length > 0 ? (
          <TherapeuticAlternativesSection
            alternatives={display.therapeuticAlternatives}
            onSelectAlternative={onSelectAlternative}
            isSelectingAlternative={isSelectingAlternative}
          />
        ) : null}

        {pricing ? <PricingSection pricing={pricing} /> : null}
      </div>
    </div>
  );
}

function PayerSection({ payer }: { payer: PayerInformationT }) {
  const fields = [
    { label: 'Payer', value: payer.PayerName },
    { label: 'Payer ID', value: payer.PayerId },
    { label: 'Plan', value: payer.PlanName },
    { label: 'BIN', value: payer.BIN },
    { label: 'PCN', value: payer.PCN },
    { label: 'Member ID', value: payer.MemberId },
    { label: 'Coverage ID', value: payer.CoverageId },
  ].filter((f) => f.value != null && f.value !== '');

  if (fields.length === 0) return null;

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
        <ShieldCheck className="size-3" aria-hidden />
        Coverage (271 Response)
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 @md:grid-cols-3">
        {fields.map(({ label, value }) => (
          <div key={label}>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="break-words text-xs font-medium">{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FormularySection({
  formularyStatus,
  restrictions,
  copays,
  display,
  payerInformation,
}: {
  formularyStatus: string | null | undefined;
  restrictions: CoverageRestrictionsT | null | undefined;
  copays: PayerCopayT[] | null | undefined;
  display: FormularyDisplayState;
  payerInformation: PayerInformationT | null | undefined;
}) {
  const [detailOpen, setDetailOpen] = useState(false);

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
        <Info className="size-3" aria-hidden />
        Formulary
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto h-5 gap-1 px-1.5 text-[10px] text-muted-foreground hover:text-foreground"
          onClick={() => setDetailOpen(true)}
        >
          <Eye className="size-3" aria-hidden />
          Full details
        </Button>
      </div>
      <div className="space-y-2.5">
        {formularyStatus ? (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Status</div>
            <div className="text-xs font-medium">
              {formularyStatusLabel(formularyStatus) ?? formularyStatus}
            </div>
          </div>
        ) : null}

        {!display.hideCopay && copays && copays.length > 0 ? (
          <CopaysSubsection copays={copays} />
        ) : null}

        {restrictions ? <RestrictionsSubsection restrictions={restrictions} /> : null}
      </div>

      <FormularyDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        restrictions={restrictions}
        copays={copays}
        payerInformation={payerInformation}
      />
    </div>
  );
}

function CopaysSubsection({ copays }: { copays: PayerCopayT[] }) {
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Copay</div>
      <div className="flex flex-col gap-1">
        {copays.map((copay, idx) => {
          const description = describeCopay(copay);
          if (description == null) return null;
          return (
            <div
              key={`copay-${idx}`}
              className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-md border border-border/40 bg-background/40 px-2 py-1 text-xs dark:border-border/30 dark:bg-background/20"
            >
              <span className="font-medium">{pharmacyTypeLabel(copay.PharmacyType ?? null)}</span>
              <span className="text-muted-foreground">·</span>
              <span>{description}</span>
              {copay.IsDrugSpecific === false ? (
                <span className="ml-auto text-[10px] text-muted-foreground">summary</span>
              ) : null}
              {(copay.OutOfPocketMinimum != null || copay.OutOfPocketMaximum != null) && (
                <span className="ml-auto text-[10px] text-muted-foreground">
                  OOP {copay.OutOfPocketMinimum != null ? `$${copay.OutOfPocketMinimum}` : '—'}
                  {' / '}
                  {copay.OutOfPocketMaximum != null ? `$${copay.OutOfPocketMaximum}` : '—'}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RestrictionsSubsection({ restrictions }: { restrictions: CoverageRestrictionsT }) {
  const flags: string[] = [];
  if (restrictions.HasPriorAuthorization) flags.push('Prior Auth');
  if (restrictions.HasStepTherapy) flags.push('Step Therapy');
  if (restrictions.HasQuantityLimit) flags.push('Qty Limit');
  if (restrictions.HasAgeLimit) flags.push('Age Limit');
  if (restrictions.HasMedicalNecessity) flags.push('Medical Necessity');
  if (restrictions.IsExcluded) flags.push('Excluded');
  if (restrictions.GenderRestriction != null && restrictions.GenderRestriction !== 'Unknown') {
    flags.push(`${restrictions.GenderRestriction} only`);
  }

  const stepMeds = restrictions.StepMedicationRestrictions ?? [];
  const quantityLimits = restrictions.QuantityLimitRestrictions ?? [];
  const ageLimits = restrictions.AgeLimitRestrictions ?? [];
  const messages = restrictions.Messages ?? [];
  const drugResources = restrictions.DrugResources ?? [];
  const summaryResources = restrictions.SummaryResources ?? [];

  const hasAnyDetail =
    flags.length > 0 ||
    stepMeds.length > 0 ||
    quantityLimits.length > 0 ||
    ageLimits.length > 0 ||
    messages.length > 0 ||
    drugResources.length > 0 ||
    summaryResources.length > 0;

  if (!hasAnyDetail) return null;

  return (
    <div className="space-y-2">
      {flags.length > 0 ? (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Restrictions
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {flags.map((flag) => (
              <span
                key={flag}
                className="rounded-full border border-amber-400/50 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-200"
              >
                {flag}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {stepMeds.length > 0 ? <StepMedicationsBlock stepMeds={stepMeds} /> : null}
      {quantityLimits.length > 0 ? <QuantityLimitsBlock limits={quantityLimits} /> : null}
      {ageLimits.length > 0 ? <AgeLimitsBlock limits={ageLimits} /> : null}
      {messages.length > 0 ? <MessagesBlock messages={messages} /> : null}
      {drugResources.length > 0 ? (
        <ResourcesBlock label="Drug-specific resources" resources={drugResources} />
      ) : null}
      {summaryResources.length > 0 ? (
        <ResourcesBlock label="Plan resources" resources={summaryResources} />
      ) : null}
    </div>
  );
}

function StepMedicationsBlock({ stepMeds }: { stepMeds: StepMedicationRestrictionT[] }) {
  // Cert F4A: must show step name + strength + form, and respect step
  // order. DoseSpot returns name+strength+form concatenated in
  // `DisplayName`, so we only need to sort by `StepOrder`.
  const sorted = [...stepMeds].sort((a, b) => (a.StepOrder ?? 0) - (b.StepOrder ?? 0));
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
        <ListTree className="size-3" aria-hidden />
        Step Therapy Sequence
      </div>
      <ol className="mt-1 list-decimal space-y-0.5 pl-4">
        {sorted.map((step, idx) => (
          <li key={step.Ndc ?? `step-${idx}`} className="text-xs">
            {step.DisplayName ?? step.Ndc ?? '—'}
          </li>
        ))}
      </ol>
    </div>
  );
}

function QuantityLimitsBlock({ limits }: { limits: QuantityLimitRestrictionT[] }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        Quantity Limits
      </div>
      <ul className="mt-1 space-y-0.5 text-xs">
        {limits.map((limit, idx) => (
          <li key={`qty-${idx}`}>· {describeQuantityLimit(limit)}</li>
        ))}
      </ul>
    </div>
  );
}

function describeQuantityLimit(limit: QuantityLimitRestrictionT): string {
  const amount = limit.MaximumAmount != null ? `${limit.MaximumAmount}` : '—';
  const amountUnit = limit.AmountUnit ?? '';
  const periodUnits = limit.MaximumTimePeriodUnits;
  const periodKind = limit.TimePeriodUnit ?? '';

  let phrase = `${amount} ${humanizeAmountUnit(amountUnit)}`.trim();
  if (periodUnits != null && periodKind !== '' && periodKind !== 'Lifetime') {
    phrase += ` per ${periodUnits} ${humanizePeriodUnit(periodKind)}`;
  } else if (periodKind === 'Lifetime') {
    phrase += ' for life';
  } else if (periodKind === 'PerDispensing') {
    phrase += ' per dispensing';
  } else if (periodKind === 'SpecificDateRange' && limit.StartDate && limit.EndDate) {
    phrase += ` between ${limit.StartDate} and ${limit.EndDate}`;
  }
  return phrase;
}

function humanizeAmountUnit(unit: string): string {
  switch (unit) {
    case 'Quantity':
      return 'units';
    case 'DaysSupply':
      return 'days supply';
    case 'Fills':
      return 'fills';
    case 'DollarAmount':
      return 'dollars';
    default:
      return unit;
  }
}

function humanizePeriodUnit(unit: string): string {
  switch (unit) {
    case 'Days':
      return 'days';
    case 'CalendarYear':
      return 'calendar year';
    case 'CalendarQuarter':
      return 'calendar quarter';
    case 'CalendarMonth':
      return 'calendar month';
    case 'PerDispensing':
      return 'dispensing';
    case 'SpecificDateRange':
      return 'date range';
    default:
      return unit;
  }
}

function AgeLimitsBlock({ limits }: { limits: AgeLimitRestrictionT[] }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Age Limits</div>
      <ul className="mt-1 space-y-0.5 text-xs">
        {limits.map((limit, idx) => (
          <li key={`age-${idx}`}>· {describeAgeLimit(limit)}</li>
        ))}
      </ul>
    </div>
  );
}

function describeAgeLimit(limit: AgeLimitRestrictionT): string {
  const parts: string[] = [];
  if (limit.MinimumAge != null) {
    parts.push(`min ${limit.MinimumAge} ${limit.MinimumAgeUnit?.toLowerCase() ?? 'years'}`);
  }
  if (limit.MaximumAge != null) {
    parts.push(`max ${limit.MaximumAge} ${limit.MaximumAgeUnit?.toLowerCase() ?? 'years'}`);
  }
  return parts.length > 0 ? parts.join(' / ') : 'Age limit applies';
}

function MessagesBlock({ messages }: { messages: CoverageMessageT[] }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
        <MessageSquare className="size-3" aria-hidden />
        Coverage Messages
      </div>
      <ul className="mt-1 space-y-1">
        {messages.map((msg, idx) => {
          // Cert F4D: when both short and long are present they're the
          // same record — we only need to show one. Long is preferred
          // because it carries the full clinical guidance.
          const text = msg.LongMessage ?? msg.Message ?? null;
          if (text == null || text.trim() === '') return null;
          return (
            <li
              key={`msg-${idx}`}
              className="rounded-md border border-border/40 bg-background/40 px-2 py-1 text-xs dark:border-border/30 dark:bg-background/20"
            >
              {text}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ResourcesBlock({ label, resources }: { label: string; resources: CoverageResourceT[] }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
        <FileText className="size-3" aria-hidden />
        {label}
      </div>
      <ul className="mt-1 space-y-0.5">
        {resources.map((resource, idx) => {
          if (resource.Url == null || resource.Url.trim() === '') return null;
          return (
            <li key={`resource-${idx}`} className="text-xs">
              <a
                href={resource.Url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 text-sky-700 underline-offset-2 hover:underline dark:text-sky-300"
              >
                {resource.ResourceType ?? 'Resource'}
                <ExternalLink className="size-3" aria-hidden />
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function NoticeRow({ text }: { text: string }) {
  return (
    <div className="rounded-md border border-border/40 bg-background/40 px-3 py-2 text-[11px] text-muted-foreground dark:border-border/30 dark:bg-background/20">
      {text}
    </div>
  );
}

interface ExtractedPricing {
  estimatedPatientPay: string | null;
  planPay: string | null;
  deductibleRemaining: string | null;
  oopRemaining: string | null;
  daysSupplyPriced: string | null;
  quantityPriced: string | null;
  drugStatus: string | null;
}

function extractPricing(
  benefits: PrescriptionBenefitsResponseT | null | undefined
): ExtractedPricing | null {
  const coverages = benefits?.PatientSpecificPricingCoverages;
  if (!coverages || coverages.length === 0) return null;

  const pricing = coverages[0]?.PricingCoverage;
  if (!pricing) return null;

  const result: ExtractedPricing = {
    estimatedPatientPay: pricing.EstimatedPatientPayAmount ?? null,
    planPay: pricing.PlanPayAmount ?? null,
    deductibleRemaining: pricing.DeductibleRemainingAmount ?? null,
    oopRemaining: pricing.OOPRemainingAmount ?? null,
    daysSupplyPriced: pricing.DaysSupplyPriced ?? null,
    quantityPriced: pricing.QuantityPricedValue ?? null,
    drugStatus: pricing.DrugStatus ?? null,
  };

  const hasAnyValue = Object.values(result).some((v) => v != null);
  return hasAnyValue ? result : null;
}

function PricingSection({ pricing }: { pricing: ExtractedPricing }) {
  const fields = [
    { label: 'Est. Patient Pay', value: pricing.estimatedPatientPay, prefix: '$' },
    { label: 'Plan Pay', value: pricing.planPay, prefix: '$' },
    { label: 'Deductible Remaining', value: pricing.deductibleRemaining, prefix: '$' },
    { label: 'OOP Remaining', value: pricing.oopRemaining, prefix: '$' },
    { label: 'Days Supply Priced', value: pricing.daysSupplyPriced, prefix: '' },
    { label: 'Quantity Priced', value: pricing.quantityPriced, prefix: '' },
    { label: 'Drug Status', value: pricing.drugStatus, prefix: '' },
  ].filter((f) => f.value != null && f.value !== '');

  if (fields.length === 0) return null;

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
        <DollarSign className="size-3" aria-hidden />
        Real-Time Benefits (RTPB)
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 @md:grid-cols-3">
        {fields.map(({ label, value, prefix }) => (
          <div key={label}>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="text-xs font-medium">
              {prefix}
              {value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PayerAlternativesSection({
  alternatives,
  onSelectAlternative,
  isSelectingAlternative,
}: {
  alternatives: PayerAlternativeT[];
  onSelectAlternative?: (dispensableDrugId: number) => void | Promise<void>;
  isSelectingAlternative: boolean;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
        <Pill className="size-3" aria-hidden />
        Payer Alternatives
      </div>
      <div className="flex flex-col gap-1">
        {alternatives.map((alt, idx) => {
          const dispensableDrugId = alt.DispensableDrugId ?? null;
          const rawCode = alt.RepresentativeFormularyCoverage?.FormularyStatus ?? null;
          const bucket = bucketFormularyStatus(rawCode, null);
          const status =
            formularyStatusLabel(rawCode) ??
            alt.RepresentativeFormularyCoverage?.FormularyStatusText ??
            rawCode;
          const showCopay = bucket !== 'unknown' && bucket !== 'not-reimbursable';
          const copay = showCopay
            ? pickAlternativeRetailCopay(alt.RepresentativeFormularyCoverage?.CopayFactors)
            : null;
          const isClickable =
            onSelectAlternative !== undefined &&
            dispensableDrugId !== null &&
            !isSelectingAlternative;
          return (
            <AlternativeRow
              key={alt.Ndc ?? `payer-alt-${idx}`}
              displayName={alt.DisplayName ?? alt.Ndc ?? '—'}
              brand={alt.Brand}
              status={status}
              copay={copay}
              isClickable={isClickable}
              onSelect={
                onSelectAlternative && dispensableDrugId !== null
                  ? () => onSelectAlternative(dispensableDrugId)
                  : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
}

export function TherapeuticAlternativesSection({
  alternatives,
  onSelectAlternative,
  isSelectingAlternative,
}: {
  alternatives: TherapeuticAlternativesT[];
  onSelectAlternative?: (dispensableDrugId: number) => void | Promise<void>;
  isSelectingAlternative: boolean;
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-foreground/80">
        <Pill className="size-3" aria-hidden />
        Therapeutic Alternatives
      </div>
      <div className="flex flex-col gap-1">
        {alternatives.map((alt, idx) => {
          const dispensableDrugId = alt.DispensableDrugId ?? null;
          const rawCode = alt.FormularyStatus ?? null;
          const bucket = bucketFormularyStatus(rawCode, null);
          const status =
            formularyStatusLabel(rawCode) ?? alt.FormularyInfo?.FormularyStatusMessage ?? rawCode;
          const showCopay = bucket !== 'unknown' && bucket !== 'not-reimbursable';
          const copay = showCopay ? (alt.Copay ?? null) : null;
          const isClickable =
            onSelectAlternative !== undefined &&
            dispensableDrugId !== null &&
            !isSelectingAlternative;
          return (
            <AlternativeRow
              key={alt.Ndc ?? `therapeutic-alt-${idx}`}
              displayName={alt.FullDisplayString ?? alt.DisplayName ?? alt.Ndc ?? '—'}
              brand={alt.FormularyInfo?.IsGeneric != null ? !alt.FormularyInfo.IsGeneric : null}
              status={status}
              copay={copay}
              isClickable={isClickable}
              onSelect={
                onSelectAlternative && dispensableDrugId !== null
                  ? () => onSelectAlternative(dispensableDrugId)
                  : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
}

function AlternativeRow({
  displayName,
  brand,
  status,
  copay,
  isClickable,
  onSelect,
}: {
  displayName: string;
  brand: boolean | null | undefined;
  status: string | null;
  copay: PayerCopayT | null;
  isClickable: boolean;
  onSelect: (() => void) | undefined;
}) {
  const copayDescription = copay ? describeCopay(copay) : null;
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 rounded-md border border-border/40 bg-background/40 px-2.5 py-1.5',
        'dark:border-border/30 dark:bg-background/20'
      )}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="truncate text-xs font-medium">{displayName}</div>
        <div className="flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
          {brand != null ? <span>{brand ? 'Brand' : 'Generic'}</span> : null}
          {status ? <span>· {status}</span> : null}
          {copayDescription ? <span>· {copayDescription}</span> : null}
        </div>
      </div>
      {isClickable && onSelect ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          radius="pill"
          onClick={onSelect}
          aria-label={`Switch to ${displayName}`}
        >
          <span className="text-[11px]">Use this</span>
          <ArrowRight className="ml-1 size-3" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}
